"use client";

import { useState } from "react";
import { Check, Clock, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 0,
  number: "01",
  title: "Discovery & audit",
  shortTitle: "01 Discovery & audit",
  summary: "Comprehensive baseline assessment of your current AI visibility & website health.",
  description: "We analyze how major AI engines—such as ChatGPT, Perplexity, and Gemini—perceive your local service business. We evaluate structured schema, citation clarity, and technical performance gaps.",
  deliv1: "AI Engine Visibility & Citation Audit",
  deliv2: "Website Technical & Schema Baseline",
  deliv3: "Customized 90-Day Priority Roadmap",
  timeline: "Week 1"
}, {
  id: 1,
  number: "02",
  title: "Design & architecture",
  shortTitle: "02 Design & architecture",
  summary: "Structuring web content and structured data for max AI comprehension.",
  description: "We architect conversion-focused landing pages and embed precise entity markup. This ensures AI search models index your key service offerings accurately and direct high-intent prospects to you.",
  deliv1: "AI-Friendly Content Wireframes",
  deliv2: "Comprehensive JSON-LD Schema Architecture",
  deliv3: "High-Intent Call-To-Action Framework",
  timeline: "Week 2 - 3"
}, {
  id: 2,
  number: "03",
  title: "Build & optimize",
  shortTitle: "03 Build & optimize",
  summary: "Technical execution, speed optimization, and automated review hooks.",
  description: "Our team builds lightning-fast, clean pages, deploys automated review collection workflows, and integrates continuous lead capture systems to turn AI referrals into scheduled calls.",
  deliv1: "Speed-Optimized Web Page Build",
  deliv2: "Automated Review & Reputation Engine",
  deliv3: "Lead & Booking System Integration",
  timeline: "Week 3 - 5"
}, {
  id: 3,
  number: "04",
  title: "Launch & measure",
  shortTitle: "04 Launch & measure",
  summary: "Going live with active performance tracking and monthly LLM reports.",
  description: "We launch your updated infrastructure and continuously track your prompt inclusion metrics. Monthly reporting keeps you updated on exact AI query rankings and conversion performance.",
  deliv1: "Full Infrastructure Production Launch",
  deliv2: "Monthly AI Citation & Ranking Report",
  deliv3: "Iterative Optimization & Keyword Scaling",
  timeline: "Ongoing / Monthly"
}];
export default function Stepper() {
  const [activeStep, setActiveStep] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  const current = steps[activeStep];
  return <section data-section-id="31004" id="interactiveprocessstepper" ref={ref} className={`relative bg-background py-24 md:py-36 text-foreground overflow-hidden transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-[var(--accent-deep-red)]/40 bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] px-3 py-1 text-xs tracking-wider uppercase font-semibold rounded-md">
            OUR PROCESS
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            How We Make You <span className="font-serif italic text-[var(--accent-deep-red)]">Unstoppable</span> in AI Search
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans">
            A clear, 4-step framework built to replace guesswork with predictable AI rankings, automated reviews, and qualified leads.
          </p>
        </div>

        <div className="hidden md:block">
          <div className="relative mb-16 px-6">
            <div className="absolute top-6 left-12 right-12 h-0.5 bg-border rounded-lg" />
            
            <div className="absolute top-6 left-12 h-0.5 bg-[var(--accent-deep-red)] transition-all duration-500 rounded-lg" style={{
            width: `calc(${activeStep / (steps.length - 1) * 100}% - 3rem)`
          }} />

            <div className="relative flex justify-between items-start">
              {steps.map((step, i) => {
              const isActive = activeStep === i;
              const isPassed = activeStep >= i;
              return <div key={step.id} data-index={i} className="flex flex-col items-center group">
                    <Button variant="outline" size="icon" onClick={() => setActiveStep(i)} className={`relative z-10 size-12 rounded-lg font-bold text-sm transition-all duration-300 border-2 cursor-pointer shadow-md ${isActive ? "bg-[var(--accent-deep-red)] border-[var(--accent-deep-red)] text-white shadow-lg scale-110" : isPassed ? "bg-background border-[var(--accent-deep-red)] text-[var(--accent-deep-red)]" : "bg-background border-border text-muted-foreground hover:border-foreground/40"}`}>
                      {step.number}
                    </Button>
                    <Button variant="ghost" onClick={() => setActiveStep(i)} className="mt-3 text-sm font-default font-semibold transition-colors hover:bg-transparent cursor-pointer p-0 h-auto">
                      <span className={isActive ? "text-[var(--accent-deep-red)] font-bold" : "text-muted-foreground"}>
                        {step.title}
                      </span>
                    </Button>
                  </div>;
            })}
            </div>
          </div>

          <div className="relative min-h-[320px]">
            <Card className="rounded-2xl border border-border bg-[hsl(240_10%_7%)] text-foreground shadow-xl transition-all duration-300 opacity-100 translate-y-0">
              <CardContent className="p-8 md:p-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-7 space-y-6">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl font-bold font-serif text-[var(--accent-deep-red)]">
                        {current.number}
                      </span>
                      <h3 className="text-2xl font-default font-bold text-white">
                        {current.title}
                      </h3>
                    </div>
                    
                    <p className="text-muted-foreground text-base leading-relaxed font-sans">
                      {current.description}
                    </p>

                    <div className="flex items-center gap-2 text-sm text-[var(--accent-deep-red)] font-medium bg-[var(--accent-deep-red)]/10 px-4 py-2 rounded-lg w-fit border border-[var(--accent-deep-red)]/20">
                      <Clock className="size-4" />
                      <span>Estimated Timeline: {current.timeline}</span>
                    </div>
                  </div>

                  <div className="lg:col-span-5 border-t lg:border-t-0 lg:border-l border-border/60 pt-6 lg:pt-0 lg:pl-8">
                    <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                      <Sparkles className="size-4 text-[var(--accent-deep-red)]" />
                      Key Deliverables
                    </h4>
                    <ul className="space-y-3">
                      <li data-index={0} className="flex items-start gap-3 text-sm text-foreground">
                        <div className="mt-0.5 rounded-md p-1 bg-[var(--accent-deep-red)]/20 text-[var(--accent-deep-red)]">
                          <Check className="size-3.5" />
                        </div>
                        <span>{current.deliv1}</span>
                      </li>
                      <li data-index={1} className="flex items-start gap-3 text-sm text-foreground">
                        <div className="mt-0.5 rounded-md p-1 bg-[var(--accent-deep-red)]/20 text-[var(--accent-deep-red)]">
                          <Check className="size-3.5" />
                        </div>
                        <span>{current.deliv2}</span>
                      </li>
                      <li data-index={2} className="flex items-start gap-3 text-sm text-foreground">
                        <div className="mt-0.5 rounded-md p-1 bg-[var(--accent-deep-red)]/20 text-[var(--accent-deep-red)]">
                          <Check className="size-3.5" />
                        </div>
                        <span>{current.deliv3}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="block md:hidden space-y-6">
          {steps.map((step, i) => {
          const isActive = activeStep === i;
          return <div key={step.id} data-index={i} className="relative">
                <Card className={`rounded-2xl border transition-all duration-300 ${isActive ? "border-[var(--accent-deep-red)]/60 bg-[hsl(240_10%_7%)] text-foreground shadow-lg" : "border-border/60 bg-card text-card-foreground opacity-90"}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`flex items-center justify-center size-9 rounded-lg font-bold text-sm ${isActive ? "bg-[var(--accent-deep-red)] text-white" : "bg-muted text-muted-foreground"}`}>
                          {step.number}
                        </span>
                        <h3 className="text-lg font-default font-bold text-foreground">
                          {step.title}
                        </h3>
                      </div>
                      <Button variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveStep(i)} className={`text-xs rounded-md ${isActive ? "bg-[var(--accent-deep-red)] text-white" : "text-muted-foreground"}`}>
                        {isActive ? "Active" : "View"}
                      </Button>
                    </div>

                    {isActive ? <div className="space-y-4 pt-3 border-t border-border/40 mt-3 animate-in fade-in duration-300">
                        <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                          {step.description}
                        </p>

                        <div className="pt-2">
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
                            <Sparkles className="size-3.5 text-[var(--accent-deep-red)]" />
                            Deliverables
                          </h4>
                          <ul className="space-y-2.5">
                            <li data-index={0} className="flex items-start gap-2.5 text-xs text-foreground">
                              <div className="mt-0.5 rounded-md p-0.5 bg-[var(--accent-deep-red)]/20 text-[var(--accent-deep-red)]">
                                <Check className="size-3" />
                              </div>
                              <span>{step.deliv1}</span>
                            </li>
                            <li data-index={1} className="flex items-start gap-2.5 text-xs text-foreground">
                              <div className="mt-0.5 rounded-md p-0.5 bg-[var(--accent-deep-red)]/20 text-[var(--accent-deep-red)]">
                                <Check className="size-3" />
                              </div>
                              <span>{step.deliv2}</span>
                            </li>
                            <li data-index={2} className="flex items-start gap-2.5 text-xs text-foreground">
                              <div className="mt-0.5 rounded-md p-0.5 bg-[var(--accent-deep-red)]/20 text-[var(--accent-deep-red)]">
                                <Check className="size-3" />
                              </div>
                              <span>{step.deliv3}</span>
                            </li>
                          </ul>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-[var(--accent-deep-red)] font-medium bg-[var(--accent-deep-red)]/10 px-3 py-1.5 rounded-lg w-fit">
                          <Clock className="size-3.5" />
                          <span>Timeline: {step.timeline}</span>
                        </div>
                      </div> : <p className="text-xs text-muted-foreground line-clamp-1 font-sans mt-1">
                        {step.summary}
                      </p>}
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}